import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { reformatPhoneNumber } from '../../utils/utils';
import { submitApplication } from '../../utils/api';
import { z } from 'zod';
import AccommodationForm from './AccommodationForm';
import ActivitiesForm from './ActivitiesForm';
import MainDialog from './MainDialog';
import PaymentForm from './PaymentForm';
import PersonalInfoForm from './PersonalInfoForm';
import ReviewForm from './ReviewForm';
import { clearDraft, loadDraft, mergeDefaults, saveDraft } from './draft';
import SuspensionForm from './SuspensionForm';

// 🔹 Main form schema
export const schema = z.object({
  general: z
    .object({
      certificateName: z.string().min(2, 'err_required'),
      fullName: z.string().min(2, 'err_required'),
      preferredName: z.string().optional(),
      instagramProfile: z.string().optional(),
      email: z.email('err_email'),
      confirmEmail: z.email('err_email'),
      // phoneNumber: z.string().min(5, 'err_required'),
      emergencyContact: z.string().min(2, 'err_required'),
      // age: z.string().min(2, 'err_required'),
      age: z.string().refine((val) => val === 'TAK', {
        message: 'err_adult',
      }),
      shirtSize: z.string().min(1, 'err_required'),
      attendedPreviousEdition: z.string().min(2, 'err_required'),
      travelMethod: z.string().min(2, 'err_required'),
      useWhatsApp: z.string().min(2, 'err_required'),
      allowGroupContact: z.string().min(2, 'err_required'),
      imageConsent: z.string().min(2, 'err_required'),
    })
    .refine((data) => data.email === data.confirmEmail, {
      path: ['confirmEmail'], // error will show on confirmEmail
      message: 'err_email_match',
    }),
  accommodationPreferences: z.object({
    diet: z.string().min(2, 'err_required'),
    dietOther: z.string().optional(),
    dairyProducts: z.string().min(2, 'err_required'),
    accommodationPreferences: z.string().optional(),
    healthSafetyInfo: z.string().optional(),
  }),
  eventActivities: z.object({
    saunaConsent: z.string().min(2, 'err_required'),
    yogaInterest: z.string().min(2, 'err_required'),
    hikingInterest: z.string().min(2, 'err_required'),
    hikingAlternative: z.string().optional(),
    hookRitualInterest: z.string().min(2, 'err_required'),
    playPiercingInterest: z.string().min(2, 'err_required'),
    piercersForPiercers: z.string().optional(),
    piercersForPiercersDescription: z.string().optional(),
    shibariWorkshopInterest: z.string().min(2, 'err_required'),
    shibariSkillLevel: z.string().optional(),
    shibariLectureInterest: z.string().min(2, 'err_required'),
    shibariLectureInterestDesc: z.string().optional(),
  }),
  bodySuspension: z.object({
    suspensionInterest: z.string().min(2, 'err_required'),
    firstTimeSuspension: z.string().min(2, 'err_required'),
    suspensionPosition: z.string().optional(),
    jointSuspension: z.string().min(2, 'err_required'),
    jointSuspensionPartner: z.string().optional(),
    privacyPreference: z.string().optional(),
    suspensionIntention: z.string().optional(),
    suspensionVision: z.string().optional(),
    suspensionHealthInfo: z.string().optional(),
  }),
  paymentDetails: z.object({
    paymentName: z.string().min(1, 'err_required'),
    paymentSurname: z.string().min(1, 'err_required'),
    addressLine1: z.string().min(1, 'err_required'),
    locality: z.string().min(1, 'err_required'),
    postalCode: z.string().min(1, 'err_required'),
    countryCode: z.string().min(1, 'err_required'),
    countryCodePayment: z.string().min(1, 'err_required'),
    tel: z.string().min(5, 'err_required'),
    acceptTerms: z.coerce.boolean().refine((val) => val === true, {
      message: 'err_terms',
    }),
  }),
});

const DEFAULT_VALUES = {
  general: {
    certificateName: '',
    fullName: '',
    preferredName: '',
    instagramProfile: '',
    email: '',
    confirmEmail: '',
    // phoneNumber: '',
    emergencyContact: '',
    age: '',
    shirtSize: '',
    attendedPreviousEdition: '',
    travelMethod: '',
    useWhatsApp: '',
    allowGroupContact: '',
    imageConsent: '',
  },
  accommodationPreferences: {
    diet: '',
    dietOther: '',
    dairyProducts: '',
    accommodationPreferences: '',
    healthSafetyInfo: '',
  },
  eventActivities: {
    saunaConsent: '',
    yogaInterest: '',
    hikingInterest: '',
    hikingAlternative: '',
    hookRitualInterest: '',
    playPiercingInterest: '',
    piercersForPiercers: '',
    piercersForPiercersDescription: '',
    shibariWorkshopInterest: '',
    shibariSkillLevel: '',
    shibariLectureInterest: '',
    shibariLectureInterestDesc: '',
  },
  bodySuspension: {
    suspensionInterest: '',
    firstTimeSuspension: '',
    suspensionPosition: '',
    jointSuspension: '',
    jointSuspensionPartner: '',
    privacyPreference: '',
    suspensionIntention: '',
    suspensionVision: '',
    suspensionHealthInfo: '',
  },
  paymentDetails: {
    paymentName: '',
    paymentSurname: '',
    addressLine1: '',
    locality: '',
    postalCode: '',
    countryCode: '',
    countryCodePayment: '',
    tel: '',
    acceptTerms: '',
  },
};

export default function BookingDialog() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  // answers typed earlier in this tab (kept in sessionStorage)
  const [draft] = useState(loadDraft);
  const stepRef = useRef(draft?.step ?? 0);

  const methods = useForm({
    defaultValues: mergeDefaults(DEFAULT_VALUES, draft?.values),
    resolver: zodResolver(schema),
    mode: 'onTouched',
    shouldUnregister: '',
  });

  const [restored, setRestored] = useState(!!draft);

  // nothing typed yet -> nothing worth keeping
  const persist = () => {
    const values = methods.getValues();
    if (JSON.stringify(values) === JSON.stringify(DEFAULT_VALUES)) clearDraft();
    else saveDraft(values, stepRef.current);
  };

  useEffect(() => {
    const sub = methods.watch((_values, { name }) => {
      if (name) persist(); // reset() has no field name
    });
    return () => sub.unsubscribe();
  }, []);

  const startOver = () => {
    clearDraft();
    stepRef.current = 0;
    methods.reset(DEFAULT_VALUES);
    setRestored(false);
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // 🔹 Step validation
  const validateStep = async (step) => {
    let fieldsToValidate = [];
    if (step === 0) {
      // Validate "general" fields
      const attendedPreviousEdition = methods.watch(
        'general.attendedPreviousEdition',
      );
      fieldsToValidate = [
        'general.certificateName',
        'general.fullName',
        'general.email',
        'general.confirmEmail',
        // 'general.phoneNumber',
        'general.emergencyContact',
        'general.age',
        'general.shirtSize',
        'general.attendedPreviousEdition',
        'general.imageConsent',
      ];
      if (attendedPreviousEdition === 'NIE') {
        fieldsToValidate.push(
          'general.travelMethod',
          'general.useWhatsApp',
          'general.allowGroupContact',
        );
      }
    } else if (step === 1) {
      // Validate "accommodationPreferences"
      fieldsToValidate = [
        'accommodationPreferences.diet',
        'accommodationPreferences.dietOther',
        'accommodationPreferences.dairyProducts',
        'accommodationPreferences.accommodationPreferences',
        'accommodationPreferences.healthSafetyInfo',
      ];
    } else if (step === 2) {
      // Validate "eventActivities"
      fieldsToValidate = [
        'eventActivities.saunaConsent',
        'eventActivities.yogaInterest',
        'eventActivities.hikingInterest',
        'eventActivities.hikingAlternative',
        'eventActivities.hookRitualInterest',
        'eventActivities.playPiercingInterest',
        'eventActivities.piercersForPiercers',
        'eventActivities.piercersForPiercersDescription',
        'eventActivities.shibariWorkshopInterest',
        'eventActivities.shibariSkillLevel',
        'eventActivities.shibariLectureInterest',
      ];
    } else if (step === 3) {
      // Validate "bodySuspension"
      const suspensionInterest = methods.watch(
        'bodySuspension.suspensionInterest',
      );
      fieldsToValidate = ['bodySuspension.suspensionInterest'];
      if (suspensionInterest === 'TAK') {
        fieldsToValidate.push(
          'bodySuspension.firstTimeSuspension',
          'bodySuspension.suspensionPosition',
          'bodySuspension.jointSuspension',
          'bodySuspension.jointSuspensionPartner',
          'bodySuspension.privacyPreference',
          'bodySuspension.suspensionIntention',
          'bodySuspension.suspensionVision',
          'bodySuspension.suspensionHealthInfo',
        );
      }
    } else if (step === 4) {
      // Validate "paymentDetails"
      fieldsToValidate = [
        'paymentDetails.paymentName',
        'paymentDetails.paymentSurname',
        'paymentDetails.addressLine1',
        'paymentDetails.locality',
        'paymentDetails.postalCode',
        'paymentDetails.countryCode',
        'paymentDetails.countryCodePayment',
        'paymentDetails.tel',
      ];
    } else if (step === 5) {
      // Review: consent is given on the summary
      fieldsToValidate = ['paymentDetails.acceptTerms'];
    }

    return await methods.trigger(fieldsToValidate, { shouldFocus: true });
  };

  const handleSubmit = async () => {
    setSubmitError('');
    if (!(await validateStep(5))) {
      setSubmitError(t('fill_mandatory'));
      return;
    }

    setSubmitting(true);
    try {
      const values = methods.getValues();
      const tel = reformatPhoneNumber(
        values.paymentDetails.countryCode,
        values.paymentDetails.tel ?? '',
      );
      await submitApplication({
        ...values,
        paymentDetails: { ...values.paymentDetails, tel },
        lang,
        amount: 0,
        locationId: process.env.REACT_APP_SQUARE_LOCATION_ID,
        token: '',
      });
      clearDraft();
      navigate(`/${lang}/${t('register')}/success`);
    } catch (err) {
      setSubmitError(err.message || t('submit_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <MainDialog
        title={t('register_label')}
        steps={[
          t('section1_title'),
          t('section2_title'),
          t('section3_title'),
          t('section4_title'),
          t('section5_title'),
          t('section6_title'),
        ]}
        open={open}
        setOpen={setOpen}
        validateStep={validateStep}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitError={submitError}
        isDirty={methods.formState.isDirty || restored}
        initialStep={draft?.step ?? 0}
        onStepChange={(step) => {
          stepRef.current = step;
          persist();
        }}
        restored={restored}
        onStartOver={startOver}
        onDiscard={clearDraft}
      >
        <PersonalInfoForm />
        <AccommodationForm />
        <ActivitiesForm />
        <SuspensionForm />
        <PaymentForm />
        <ReviewForm />
      </MainDialog>
    </FormProvider>
  );
}
